import {
  getSignatureHtml,
  SIGNATURE_MARKER_ATTR,
  SignatureTemplateData
} from "../signature/signatureTemplate";
import { getUserProfileFromGraph, GraphUserProfile } from "./graphService";

declare const Office: any;

export interface SignaturePayload {
  name: string;
  jobTitle: string;
  company: string;
  websiteUrl: string;
  phone: string;
  html: string;
  markerAttribute: string;
}

/**
 * Signature service abstraction.
 *
 * Stage 2.x implementation fetches user data from Microsoft Graph.
 * Falls back to hardcoded profiles if Graph fails.
 *
 * Future stages can add backend API, database, or other signature sources.
 */
export class SignatureService {
  public getCurrentUserEmailAddress(): string {
    return Office.context.mailbox?.userProfile?.emailAddress?.trim().toLowerCase() ?? "";
  }

  /**
   * Returns comma-separated list of allowed emails.
   * Stage 2.x: Returns "All company users (via Microsoft Graph)"
   */
  public getAllowedEmailAddress(): string {
    return "All company users (via Microsoft Graph)";
  }

  /**
   * All company users are now allowed.
   * Dynamic signature is fetched per user from Graph.
   */
  public isSignatureEnabledForCurrentUser(): boolean {
    return true; // All users enabled in dynamic mode
  }

  /**
   * Fetches current user's signature data from Microsoft Graph.
   *
   * Flow:
   * 1. Fetch user profile from Graph API (with localStorage cache)
   * 2. Build signature template using Graph data
   * 3. Generate HTML
   * 4. Return SignaturePayload
   *
   * If Graph fetch fails, throws error (caller decides fallback).
   */
  public async getSignatureForCurrentUser(): Promise<SignaturePayload> {
    let templateData: SignatureTemplateData;

    try {
      const profile = await getUserProfileFromGraph();
      templateData = this.graphProfileToTemplateData(profile);
      console.log("[SignatureService] Signature generated from Microsoft Graph for:", templateData.name);
    } catch (error) {
      console.warn("[SignatureService] Graph fetch failed, using Office userProfile fallback:", error);

      // Fallback: Read user identity directly from Outlook mailbox context without requiring tokens
      const userProfile = Office.context.mailbox?.userProfile;
      const displayName = userProfile?.displayName?.trim();
      const email = userProfile?.emailAddress?.trim();

      templateData = {
        name: displayName || (email ? email.split("@")[0] : "Team Member"),
        jobTitle: "Team Member",
        company: "TMotions Global Limited",
        websiteUrl: "https://www.tmotions.com",
        phone: "N/A"
      };
      console.log("[SignatureService] Signature generated from Outlook profile fallback for:", templateData.name);
    }

    return {
      name: templateData.name,
      jobTitle: templateData.jobTitle,
      company: templateData.company,
      websiteUrl: templateData.websiteUrl,
      phone: templateData.phone,
      html: getSignatureHtml(templateData),
      markerAttribute: SIGNATURE_MARKER_ATTR
    };
  }

  /**
   * Converts Microsoft Graph user profile to signature template data.
   *
   * Extracts:
   * - displayName -> name
   * - jobTitle -> jobTitle
   * - companyName or default -> company
   * - mobilePhone or businessPhones[0] -> phone
   * - officeLocation (if available)
   */
  private graphProfileToTemplateData(profile: GraphUserProfile): SignatureTemplateData {
    // Phone: prefer mobile, fallback to first business phone
    const phone =
      profile.mobilePhone?.trim() ||
      (profile.businessPhones?.[0]?.trim() ?? "");

    return {
      name: profile.displayName || "User",
      jobTitle: profile.jobTitle || "Employee",
      company: profile.companyName || "TMotions Global Limited",
      websiteUrl: "https://www.tmotions.com",
      phone: phone || "N/A"
    };
  }
}

export const signatureService = new SignatureService();
